"use expense";

import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { advancePaymentSchema } from "@/validations";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { fetchFullCommission } from "@/store/modules/fullCommission/thunks";
import { UseDataRangeFilter } from "@/types";
import {
  createAdvancePayment,
  deleteAdvancePayment,
  fetchAdvancePayment,
  updateAdvancePayment,
} from "@/store/modules/advancePayment/thunks";
import { fetchPaymentMethods } from "@/store/modules/paymentMethod/thunks";

const useAdvancePayment = ({ dateRange, onSuccess }: UseDataRangeFilter) => {
  const { params, setParams, handlePage } = useParams();
  const { per_page, current_page } = params;
  const { currency, establishment, baseURL } = useAuth();
  const { hasPermission, loading: loadingPermission } = usePermission();

  const [advancePaymentId, setAdvancePaymentId] = useState<any>();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(advancePaymentSchema),
    defaultValues: {
      user_id: undefined,
      amount: "",
      paid_at: undefined,
      payment_method_id: undefined,
      maturity: undefined,
      description: "",
    },
  });

  const setFormData = (
    data: Partial<typeof advancePaymentSchema.__outputType>,
  ) => {
    reset(data);
  };

  const onSubmit = async (data: typeof advancePaymentSchema.__outputType) => {
    try {
      let result;

      const payload = {
        ...data,
        amount:
          typeof data.amount === "string"
            ? parseFloat(String(data.amount).replace(/,/g, "."))
            : Number(data.amount),
        establishment_id: establishment?.id,
      } as any;

      if (advancePaymentId) {
        result = await dispatch(
          updateAdvancePayment({ id: advancePaymentId, ...payload }),
        );
      } else {
        result = await dispatch(createAdvancePayment(payload));
      }

      if (
        createAdvancePayment.fulfilled.match(result) ||
        updateAdvancePayment.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        // Refresh full commission and advancePayment lists
        dispatch(
          fetchFullCommission({
            ...params,
            start_date: dateRange.start.toISOString().split("T")[0],
            end_date: dateRange.end.toISOString().split("T")[0],
          } as any),
        );

        dispatch(
          fetchAdvancePayment({
            ...params,
            filter: {
              ...(params.filter || {}),
              establishment_id: establishment?.id,
            },
          } as any),
        );

        reset();
        setAdvancePaymentId(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { advancePayment, loading, error } = useSelector(
    (state: any) => state.advancePayment,
  );
  const { paymentMethods } = useSelector((state: any) => state.paymentMethods);
  const dispatch: AppDispatch = useDispatch();

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment || hasFetchedRef.current) return;

    const payload = {
      ...params,
    };

    dispatch(fetchAdvancePayment(payload));

    // Sempre requisitar métodos de pagamento para popular o select do modal de vales.
    // O backend dará 403 se o usuário não tiver permissão de leitura.
    dispatch(
      fetchPaymentMethods({
        without_pagination: 1,
        filter: { establishment_id: establishment?.id },
      }),
    );

    hasFetchedRef.current = true;
  }, [baseURL, establishment, dispatch, params, hasPermission]);

  const handleDelete = async () => {
    try {
      if (advancePaymentId) {
        await dispatch(deleteAdvancePayment(advancePaymentId));
        setAdvancePaymentId(null);

        return true;
      }
    } catch (error) {
      console.error("Error deleting advance payment:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    loading,
    error,
    advancePayment,
    setAdvancePaymentId,
    handleDelete,
    setFormData,
    currency,
    control,
    paymentMethods,
  };
};

export default useAdvancePayment;
